import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      category: "Getting Started",
      faqs: [
        {
          question: "What types of projects does Fearless IT work on?",
          answer: "We specialize in custom SaaS application development, web and mobile applications, cloud infrastructure, and technical consulting. Our expertise spans from MVP development for startups to enterprise-scale platforms for established businesses."
        },
        {
          question: "How do I get started with a project?",
          answer: "Getting started is easy! Simply visit our Contact page or click 'Get Started' to schedule a free consultation. We'll discuss your project requirements, timeline, and budget to create a tailored proposal for your needs."
        },
        {
          question: "Do you offer free consultations?",
          answer: "Yes! We offer a free initial consultation to understand your project requirements and determine if we're a good fit. This includes a high-level assessment of your needs and a preliminary discussion of approach and timeline."
        },
        {
          question: "What information do I need to provide for a quote?",
          answer: "To provide an accurate quote, we typically need: a description of your project goals, target users, key features required, any existing systems to integrate with, your timeline expectations, and budget range. The more detail you can provide, the more accurate our estimate will be."
        }
      ]
    },
    {
      category: "Pricing & Packages",
      faqs: [
        {
          question: "What are your pricing tiers?",
          answer: "We offer three main development tiers: Essentials ($15,000 – $35,000) for MVPs and early-stage products, Plus ($40,000 – $85,000) for growing SaaS products, and PRO (Custom Pricing) for enterprise and complex platforms. Each tier includes different levels of features, support, and timeline."
        },
        {
          question: "What's included in the hosting packages?",
          answer: "Our hosting packages range from Essentials ($150 – $450/month) for single environment setups, Plus ($500 – $1,500/month) for multi-environment with enhanced monitoring, to PRO ($2,000 – $8,000+/month) for high-availability enterprise hosting with advanced security and compliance features."
        },
        {
          question: "Do you offer ongoing support?",
          answer: "Yes! We offer ongoing support packages starting from Essentials ($1,000 – $2,500/month) for basic maintenance, Plus ($3,000 – $6,500/month) for priority support with feature enhancements, and PRO ($8,000 – $18,000+/month) for dedicated enterprise support. We also offer hourly support at $175 – $225/hour for ad-hoc needs."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept bank transfers, credit cards, and ACH payments. For larger projects, we typically structure payments as a deposit upfront, followed by milestone-based payments throughout the project."
        },
        {
          question: "Are there any hidden fees?",
          answer: "No hidden fees. Our proposals clearly outline all costs including development, hosting, third-party services, and any ongoing maintenance. Any scope changes during the project are discussed and approved before additional work begins."
        }
      ]
    },
    {
      category: "Development Process",
      faqs: [
        {
          question: "What is your development process?",
          answer: "Our process follows these phases: Discovery & Planning (requirements gathering, technical architecture), Design (UI/UX design, prototyping), Development (agile sprints with regular demos), Testing (QA, security testing), and Launch (deployment, monitoring setup). We maintain transparent communication throughout with regular updates and demos."
        },
        {
          question: "How long does a typical project take?",
          answer: "Timeline varies by scope: Essentials projects typically take 4-8 weeks, Plus projects 8-14 weeks, and PRO/Enterprise projects 3-6+ months. We'll provide a detailed timeline estimate during the proposal phase based on your specific requirements."
        },
        {
          question: "Will I be able to see progress during development?",
          answer: "Absolutely! We provide regular demos and updates throughout development. You'll have access to a staging environment where you can see and test new features as they're built. We typically schedule weekly or bi-weekly check-ins depending on project size."
        },
        {
          question: "What technologies do you use?",
          answer: "We use modern, scalable technologies including React, Node.js, Python, cloud platforms (AWS, GCP, Azure), and various databases (PostgreSQL, MongoDB). We select the best technology stack based on your project's specific needs and long-term scalability requirements."
        },
        {
          question: "Do you provide documentation?",
          answer: "Yes, all projects include comprehensive documentation covering: technical architecture, API documentation, deployment guides, and user guides where applicable. This ensures your team can maintain and extend the system after launch."
        }
      ]
    },
    {
      category: "After Launch",
      faqs: [
        {
          question: "What happens after my project launches?",
          answer: "After launch, we provide a warranty period (typically 30-90 days depending on your package) for bug fixes. We also offer ongoing support packages for continued maintenance, feature development, and technical support."
        },
        {
          question: "Do I own the code?",
          answer: "Yes! Upon full payment, you own all custom code developed specifically for your project. This includes full source code, documentation, and deployment configurations. We provide a non-exclusive license for any pre-existing frameworks or components we've incorporated."
        },
        {
          question: "Can I host the application myself?",
          answer: "Yes, you can host the application on your own infrastructure or with any cloud provider. We provide deployment documentation and can assist with the initial setup. However, we recommend our managed hosting for optimal performance and support."
        },
        {
          question: "How do you handle updates and maintenance?",
          answer: "Our support packages include regular maintenance such as security updates, dependency updates, and performance monitoring. For clients on support plans, we proactively monitor and address issues before they impact your users."
        },
        {
          question: "How do I cancel services?",
          answer: <>To cancel any services, please visit our <Link to="/contact" className="text-electric-indigo hover:underline font-medium">Contact page</Link> and submit a cancellation request. Our team will process your request and confirm the cancellation within 2 business days. For active subscriptions, cancellations take effect at the end of your current billing period.</>
        }
      ]
    },
    {
      category: "Security & Compliance",
      faqs: [
        {
          question: "How do you ensure security?",
          answer: "Security is built into every layer: secure coding practices, regular security audits, encryption at rest and in transit, access controls, and compliance with industry standards. Our PRO tier includes advanced security features like WAF, SSO/SAML, and compliance-ready configurations."
        },
        {
          question: "Can you build HIPAA or SOC2 compliant applications?",
          answer: "Yes! Our PRO tier includes compliance-ready hosting patterns and we have experience building applications that meet HIPAA, SOC2, and other regulatory requirements. We work with you to understand your compliance needs and implement appropriate controls."
        },
        {
          question: "How do you handle data privacy?",
          answer: "We follow data privacy best practices and can implement features like data encryption, access logging, data retention policies, and GDPR compliance features. We sign NDAs and data processing agreements as needed."
        }
      ]
    }
  ];

  let globalIndex = 0;

  return (
    <>
      <SEO 
        title="FAQ | Fearless IT"
        description="Find answers to frequently asked questions about Fearless IT services, pricing, development process, and more."
        url="/faq"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/7622e441b94768689c4d22758cecd2c8671758229efb3dccaa51bf74fab31d56.png"
      />
      <div className="min-h-screen bg-white">
        <MarketingHeader />
        
        {/* Hero Section */}
        <section className="pt-12 pb-6 sm:pt-16 sm:pb-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-black mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Find answers to common questions about our services, process, and pricing.
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {faqCategories.map((category, catIndex) => (
              <div key={catIndex} className="mb-10">
                <h2 className="text-2xl font-bold text-carbon-black mb-6 pb-2 border-b-2 border-electric-indigo">
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => {
                    const currentIndex = globalIndex++;
                    return (
                      <div 
                        key={faqIndex}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(currentIndex)}
                          className="w-full flex items-center justify-between p-4 sm:p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                          data-testid={`faq-toggle-${currentIndex}`}
                        >
                          <span className="font-semibold text-carbon-black pr-4">{faq.question}</span>
                          {openIndex === currentIndex ? (
                            <ChevronUp className="h-5 w-5 text-electric-indigo flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-gray flex-shrink-0" />
                          )}
                        </button>
                        {openIndex === currentIndex && (
                          <div className="px-4 sm:px-5 pb-4 sm:pb-5 bg-gray-50">
                            <p className="text-slate-gray leading-relaxed">
                              {typeof faq.answer === 'string' ? faq.answer : faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Contact CTA */}
            <div className="mt-12 bg-gradient-to-r from-electric-indigo to-fearless-blue rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-3">Still have questions?</h3>
              <p className="mb-6 opacity-90">We're here to help. Reach out and we'll get back to you within 24 hours.</p>
              <a 
                href="/contact"
                className="inline-block bg-white text-electric-indigo font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
};

export default FAQPage;
